<template>
    <div
        class="question-row question-row-select"
        :data-lead-service-name="name"
    >
        <transition name="floating-label">
            <p
                v-if="hasFloatingLabel && fieldValue !== ''"
                class="input__floating-label"
            >
                {{ props.content.placeholder.value }}
            </p>
        </transition>

        <div
            :id="`select-question-${numberOfSameQuestions(props.content.type)}`"
            class="select"
            :class="(hasFloatingLabel && fieldValue !== '') && 'input--floating-label'"
        >
            <svg
                class="arrow-wrapper"
                x="0px"
                y="0px"
                viewBox="0 0 80 80"
            >
                <path class="arrow select-arrow-1" d="M49.1,32.3H30.9c-2.8,0-4.3,3.4-2.3,5.5l9.1,9.1c1.2,1.2,3.3,1.2,4.5,0l9.1-9.1 C53.3,35.7,51.9,32.3,49.1,32.3z" />
                <polygon class="arrow select-arrow-2" points="28,32.4 40,47.6 52,32.4 " />
                <path class="arrow select-arrow-3" d="M41.7,50.6c4.3-4.7,8.5-9.5,12.8-14.2c1.8-2-1.1-4.9-2.9-2.9c-3.8,4.2-7.6,8.5-11.4,12.7c-3.9-4.2-7.9-8.5-11.8-12.7 c-1.8-1.9-4.6,1-2.9,2.9c4.4,4.8,8.8,9.5,13.2,14.3C39.5,51.4,40.9,51.4,41.7,50.6z" />
                <polygon class="arrow select-arrow-4" points="40,54.9 18.8,33.8 27.5,25.1 40,37.5 52.5,25.1 61.2,33.8 " />

                <g class="arrow select-arrow-5">
                    <polygon points="40,54.8 25.9,40.7 29.2,37.4 40,48.3 50.8,37.4 54.1,40.7  " />
                    <polygon points="40,42.6 25.9,28.5 29.2,25.2 40,36 50.8,25.2 54.1,28.5  " />
                </g>

                <path class="arrow select-arrow-6" d="M40,54.6c-1.6,0-3.1-0.6-4.4-1.8L18.8,35.9c-2.4-2.4-2.4-6.3,0-8.7c2.4-2.4,6.3-2.4,8.7,0L40,39.7l12.5-12.5 c2.4-2.4,6.3-2.4,8.7,0c2.4,2.4,2.4,6.3,0,8.7L44.4,52.8C43.1,54,41.6,54.6,40,54.6z" />
            </svg>

            <select
                :id="`extradata-select-question-${numberOfSameQuestions(props.content.type)}`"
                form="responseForm"
                class="question-answer"
                data-input-type="ExtraData"
                :name="`extradata-select-question-${numberOfSameQuestions(props.content.type)}`"
                @change="set"
            >
                <option value="" selected disabled hidden>
                    {{ props.content.placeholder.value }}
                </option>

                <option
                    v-for="(answer, key) in props.content.answers"
                    :key="key"
                    :data-question-to="answer.next_question?.value"
                    :data-showstopper="answer.showstopper?.value"
                    :data-hide-showstopper-close-button="answer.showstopper?.hide_showstopper_close_button || false"
                    :data-linked-form-input="answer.linked_form_input?.value"
                    :data-worktype-id="answer.worktype_id_override?.value"
                    :data-enquiry-dashboard-redirect-version="answer.enquiry_dashboard_redirect_version?.value"
                    :data-label="answer.label.value"
                    :value="answer.ELMO_value?.value"
                >
                    {{ answer.label.value }}
                </option>
            </select>
        </div>
    </div>

    <div class="question-row question-row-button">
        <button
            class="question-button-next button"
            type="button"
            value="next"
            data-showstopper="none"
            data-question-to="next_question"
            @click="check"
        >
            <zoe-atom-text
                :item="props.content.button_label.value"
            />
        </button>
    </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { addExtraData, getQuestionName, questionProps, storeExtraData } from '../../../../../mixins/quiz/v2.0.0/question';

import ZoeAtomText from '../../../../atoms/text/v1.0.0/zoe-atom-text.vue';
import { checkCompleted } from '../../../../../mixins/quiz/v2.0.0/checks';
import { getNextQuestion } from '../../../../../mixins/quiz/v2.0.0/get-next-question';
import { numberOfSameQuestions } from '../../../../../mixins/quiz/v2.0.0/helpers';
import { setInvalid } from '../../../../../mixins/quiz/v2.0.0/invalid';
import { validate } from '../../../../../js/vue-flows/default-components/sb-validate/v2/validate';
import stripHtml from '../../../../../mixins/utils/strip-html';
import getCurrentStep from '@mixins/utils/get-current-step';
import { useMask } from '../../../../../js/vue-flows/default-components/sb-mask/v2/mask';

const store = useStore();
const props = defineProps(questionProps);

const name = getQuestionName(props);
const uuid = ref(props.content?._info?.uuid);

onMounted(() => {
    addExtraData(name, {
        name,
        value: '',
        mask: '',
        validator: '',
        uuid: uuid.value,
        question: stripHtml(props.content.text.value),
        sendData: props.content.send_to_lead_service?.value !== false,
    });
});

onMounted(() => {
    storeExtraData(name, 'validator', 'notEmpty');
});

const fieldValue = computed(() => {
    return store.state.quiz.extradata[name]?.value || '';
});

const currentStep = computed(() => {
    return getCurrentStep();
});

const hasFloatingLabel = computed(() => {
    return store.getters['styling/getInputForPlacement'](currentStep.value.key, 'floatingLabel');
});

const set = event => {
    storeExtraData(name, 'value', event.target.value);
};

const check = event => {
    const questionWrapper = event.target.closest('.question');
    const input = questionWrapper.querySelector('input, select');
    const inputContainer = questionWrapper.querySelector('.question-row');
    const buttons = questionWrapper.querySelectorAll('.button');
    const delay = questionWrapper.dataset.delay ? parseInt(questionWrapper.dataset.delay) : 0;

    if (
        input.options?.[input.selectedIndex]?.dataset?.showstopper
        && input.options[input.selectedIndex].dataset.showstopper !== ''
        && input.options[input.selectedIndex].dataset.showstopper !== 'none'
    ) {
        const answer = input.options[input.selectedIndex];

        if (answer.dataset.hideShowstopperCloseButton === 'true') store.commit('dialog/preventClose');
        store.commit('dialog/show', answer.dataset.showstopper);
        return;
    }

    storeExtraData(name, 'value', input.value);
    storeExtraData(name, 'label', input.options[input.selectedIndex].dataset.label);

    if (!validate('quiz', name, input)) {
        setInvalid(inputContainer);
        return;
    }

    const answerValue = input.value;
    const linkedFormInputIdentifier = inputContainer.querySelector(`option[value="${answerValue}"]`).dataset.linkedFormInput;
    const worktypeId = inputContainer.querySelector(`option[value="${answerValue}"]`).dataset.worktypeId;
    const enquiryDashboardRedirectVersion = inputContainer.querySelector(`option[value="${answerValue}"]`).dataset.enquiryDashboardRedirectVersion;
    const nextQuestion = getNextQuestion(input.options[input.selectedIndex].dataset.questionTo, store.state.quiz.currentQuestion);

    checkCompleted(name, buttons, props.preHook, nextQuestion, props.afterHook, answerValue, linkedFormInputIdentifier, worktypeId, enquiryDashboardRedirectVersion, delay);
};
</script>

<style lang="scss">
@use 'zoe-quiz-select';
</style>
